import React, { useEffect, useRef } from 'react';
import { Input, Typography } from 'antd';
import type { TextAreaRef } from 'antd/lib/input/TextArea';

import { useEditorDoc, useEditorDispatch, useEditorUi, useEditorMeta } from '../../EditorProvider';
import { countFilled, type TraditionalDoc } from './doc';
import type { TraditionalUi } from './ui';
import { groupWords, type WordGroup } from './words';

// Uma dica por palavra, agrupadas em Horizontais (por linha) e Verticais (por coluna).
// Focar o input seleciona a palavra na grade; selecionar pela grade rola até o input.

export const Inspector: React.FC = () => {
  const doc = useEditorDoc<TraditionalDoc>();
  const { do: dispatch } = useEditorDispatch();
  const { editavel } = useEditorMeta();
  const [ui, setUi] = useEditorUi<TraditionalUi>();

  const inputs = useRef(new Map<string, TextAreaRef>());
  // Palavra cujo input está em foco: a seleção que NÃO veio dele veio da grade.
  const focusedKey = useRef<string | null>(null);

  useEffect(() => {
    const key = ui.selectedKey;
    if (key == null || focusedKey.current === key) return;
    inputs.current.get(key)?.resizableTextArea?.textArea?.scrollIntoView?.({ block: 'nearest' });
  }, [ui.selectedKey]);

  const { horizontais, verticais } = groupWords(doc);

  const section = (title: string, groups: WordGroup[]) => (
    <div style={{ marginTop: 16 }}>
      <Typography.Title level={5}>{title}</Typography.Title>
      {groups.map((g) => (
        <div key={g.label} style={{ marginBottom: 12 }}>
          <Typography.Text type="secondary">{g.label}</Typography.Text>
          {g.words.map((w) => (
            <div
              key={w.key}
              style={{
                padding: 6,
                marginTop: 4,
                borderRadius: 4,
                background: ui.selectedKey === w.key ? '#e6f4ff' : undefined,
              }}
            >
              <Typography.Text strong>{w.texto}</Typography.Text>
              <Input.TextArea
                autoSize={{ minRows: 1, maxRows: 3 }}
                ref={(el) => {
                  if (el) inputs.current.set(w.key, el);
                  else inputs.current.delete(w.key);
                }}
                aria-label={`Dica de ${w.texto}`}
                value={w.chave}
                maxLength={255}
                disabled={!editavel}
                status={w.chave.trim() === '' ? 'error' : undefined}
                onFocus={() => {
                  focusedKey.current = w.key;
                  setUi((prev) => ({ ...prev, selectedKey: w.key }));
                }}
                onBlur={() => {
                  if (focusedKey.current === w.key) focusedKey.current = null;
                }}
                onChange={(e) =>
                  dispatch({
                    type: 'SET_CHAVE',
                    key: w.key,
                    chave: e.target.value.replace(/\n/g, ' '),
                  })
                }
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ flex: 1, minWidth: 360, maxWidth: 480 }}>
      <Typography.Text strong>Título</Typography.Text>
      <Input
        aria-label="Título"
        value={doc.titulo}
        maxLength={255}
        disabled={!editavel}
        onChange={(e) => dispatch({ type: 'SET_TITULO', titulo: e.target.value })}
      />
      <div data-testid="clues-count" style={{ marginTop: 8 }}>
        {countFilled(doc)} de {doc.palavras.length} dicas preenchidas
      </div>
      {section('Horizontais', horizontais)}
      {section('Verticais', verticais)}
    </div>
  );
};
